package app.nestbridge.feature.careteam.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.components.CoreDropdown
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreTextField
import com.karawek.core.ui.theme.CoreAlphas
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.domain.identity.model.ProfessionalServiceType
import app.nestbridge.feature.careteam.screen.CreateCareTeamEvent
import app.nestbridge.feature.careteam.screen.CreateCareTeamScreen
import app.nestbridge.feature.careteam.screen.CreateCareTeamState
import app.nestbridge.feature.careteam.screen.canSubmit
import app.nestbridge.feature.children.model.Child
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import kotlinx.datetime.LocalDate
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = CreateCareTeamScreen::class,
    scope = AppScope::class,
)
@Composable
fun CreateCareTeamUi(
    state: CreateCareTeamState,
    modifier: Modifier = Modifier,
) {
    val scrollState = rememberScrollState()

    CoreScaffold(
        title = "Start a care team",
        subtitle = "Create a shared space to coordinate care for a child you support professionally.",
        footer = { Footer(state) },
        onBack = { state.eventSink(CreateCareTeamEvent.Back) },
        onAppear = { state.eventSink(CreateCareTeamEvent.OnAppear) },
        onAppearKey = state.child?.id ?: "",
        modifier = modifier,
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.lg),
            modifier = Modifier.fillMaxSize(),
        ) {
            if (state.isLoading) {
                Text(
                    text = "Loading...",
                    style = CoreTypography.body,
                    color = CoreTheme.colors.textSecondary,
                )
            } else if (state.child != null) {
                ChildInfoCard(child = state.child)

                Column(
                    verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
                ) {
                    Text(
                        text = "Care team type",
                        style = CoreTypography.body,
                        color = CoreTheme.colors.textPrimary,
                    )

                    Column(verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs)) {
                        CoreDropdown(
                            value = state.careTeamType,
                            options = ProfessionalServiceType.entries,
                            label = "",
                            placeholder = "Select a care team type",
                            onValueChange = { state.eventSink(CreateCareTeamEvent.UpdateCareTeamType(it)) },
                            itemLabel = { it.label },
                            modifier = Modifier.fillMaxWidth(),
                        )
                        Text(
                            text = "This helps us tailor goals, sessions, and reports. You can change this later.",
                            style = CoreTypography.caption,
                            color = CoreTheme.colors.textSecondary,
                        )
                    }

                    CoreTextField(
                        value = state.description,
                        onValueChange = { state.eventSink(CreateCareTeamEvent.UpdateDescription(it)) },
                        label = "Team description (optional)",
                        placeholder = "e.g., Weekly speech therapy sessions",
                        modifier = Modifier.fillMaxWidth(),
                        error = null,
                    )
                }
            }
        }
    }
}

@Composable
private fun Footer(state: CreateCareTeamState) =
    Column {
        CorePrimaryButton(
            text = if (state.isSaving) "Creating..." else "Create care team",
            enabled = state.canSubmit(),
            onClick = { state.eventSink(CreateCareTeamEvent.Submit) },
            modifier = Modifier
                .fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(CoreSpacing.sm))

        Text(
            text = "You can invite parents and other team members after the team is created.",
            style = CoreTypography.footnote,
            color = CoreTheme.colors.textSecondary,
        )
    }

@Composable
private fun ChildInfoCard(
    child: Child,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(CoreSpacing.md),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // Avatar with first letter of display name
        Box(
            modifier = Modifier
                .size(56.dp)
                .clip(CircleShape)
                .background(CoreTheme.colors.primary.copy(alpha = CoreAlphas.SelectedSurface)),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = child.effectiveDisplayName.firstOrNull()?.uppercase() ?: "?",
                style = CoreTypography.title,
                color = CoreTheme.colors.textSecondary,
            )
        }

        Column(
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
        ) {
            Text(
                text = child.fullName,
                style = CoreTypography.title,
                color = CoreTheme.colors.textPrimary,
            )
            Text(
                text = "Born ${child.formatedDateOfBirth}",
                style = CoreTypography.bodySecondary,
                color = CoreTheme.colors.textSecondary,
            )
        }
    }
}

@Preview
@Composable
private fun CreateCareTeamPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        CreateCareTeamUi(
            state = CreateCareTeamState(
                child = Child(
                    id = "1",
                    fullName = "Sofia Grace Chen",
                    displayName = "Sofia",
                    dateOfBirth = LocalDate(2023, 1, 21),
                    primaryLanguages = emptyList(),
                    stewardUserIds = emptyList(),
                    status = app.nestbridge.feature.children.model.ChildStatus.Active,
                    createdAt = kotlin.time.Clock.System.now(),
                    createdBy = "1",
                    modifiedAt = kotlin.time.Clock.System.now(),
                    modifiedBy = "1",
                ),
                careTeamType = ProfessionalServiceType.SpeechLanguageTherapy,
                description = "",
                isLoading = false,
                isSaving = false,
                eventSink = {},
            ),
        )
    }
}
